import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Calendar, ChevronDown, Edit2, Trash2 } from 'lucide-react';

interface Note {
  id: string;
  category: string;
  content: string;
  createdAt: Date;
  scheduledFor?: Date;
}

const Screen3: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState<string>('About her');
  const [noteContent, setNoteContent] = useState<string>('');
  const [notes, setNotes] = useState<Note[]>([]);
  const [showAddNote, setShowAddNote] = useState<boolean>(false);
  const [showCategoryDropdown, setShowCategoryDropdown] = useState<boolean>(false);
  const [editingNote, setEditingNote] = useState<string | null>(null);
  const [isLoaded, setIsLoaded] = useState<boolean>(false);
  const [showSchedulePicker, setShowSchedulePicker] = useState<boolean>(false);
  const [scheduledDate, setScheduledDate] = useState<string>('');
  const [scheduledTime, setScheduledTime] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  // Load notes from localStorage on component mount
  useEffect(() => {
    const savedNotes = localStorage.getItem('companion-notes');
    if (savedNotes) {
      try {
        const parsedNotes = JSON.parse(savedNotes).map((note: any) => ({
          ...note,
          createdAt: new Date(note.createdAt)
        }));
        setNotes(parsedNotes);
      } catch (error) {
        console.error('Error loading notes from localStorage:', error);
      }
    }
    setIsLoaded(true);
  }, []);

  // Save notes to localStorage whenever notes change (but not on initial load)
  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem('companion-notes', JSON.stringify(notes));
    }
  }, [notes, isLoaded]);

  const categories = [
    'About her',
    'Family & Pets',
    'Profession (Job/Education)',
    'Likes (Music, Taste)',
    'Unsorted'
  ];

  const emptyStateItems = [
    '- color of his/her eyes',
    '- Birthday',
    '- Day when you met/first date'
  ];

  const handleAddNote = () => {
    if (noteContent.trim()) {
      let scheduledFor: Date | undefined;
      if (scheduledDate && scheduledTime) {
        scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`);
      }
      
      const newNote: Note = {
        id: Date.now().toString(),
        category: selectedCategory,
        content: noteContent,
        createdAt: new Date(),
        scheduledFor
      };
      setNotes([...notes, newNote]);
      setNoteContent('');
      setScheduledDate('');
      setScheduledTime('');
      setShowAddNote(false);
    }
  };

  const handleEditNote = (noteId: string) => {
    const note = notes.find(n => n.id === noteId);
    if (note) {
      setNoteContent(note.content);
      setSelectedCategory(note.category);
      if (note.scheduledFor) {
        const date = new Date(note.scheduledFor);
        setScheduledDate(date.toISOString().split('T')[0]);
        setScheduledTime(date.toTimeString().slice(0, 5));
        setShowSchedulePicker(true); // Auto-open schedule picker for scheduled notes
      } else {
        setScheduledDate('');
        setScheduledTime('');
        setShowSchedulePicker(false);
      }
      setEditingNote(noteId);
      setShowAddNote(true);
    }
  };

  const handleUpdateNote = () => {
    if (noteContent.trim() && editingNote) {
      let scheduledFor: Date | undefined;
      if (scheduledDate && scheduledTime) {
        scheduledFor = new Date(`${scheduledDate}T${scheduledTime}`);
      }
      
      setNotes(notes.map(note => 
        note.id === editingNote 
          ? { ...note, content: noteContent, category: selectedCategory, scheduledFor }
          : note
      ));
      setNoteContent('');
      setScheduledDate('');
      setScheduledTime('');
      setEditingNote(null);
      setShowAddNote(false);
    }
  };

  const handleDeleteNote = (noteId: string) => {
    if (window.confirm('Are you sure you want to delete this note?')) {
      setNotes(notes.filter(note => note.id !== noteId));
    }
  };

  const handleCancelEdit = () => {
    setNoteContent('');
    setScheduledDate('');
    setScheduledTime('');
    setEditingNote(null);
    setShowAddNote(false);
    setSelectedCategory('About her');
  };

  // Helper function to get unique categories from notes
  const getUniqueCategories = () => {
    const uniqueCategories = [...new Set(notes.map(note => note.category))];
    return uniqueCategories;
  };

  // Check if we should show category filter (more than one category with notes)
  const shouldShowCategoryFilter = getUniqueCategories().length > 1;

  // Filter notes based on selected category
  const filteredNotes = categoryFilter === 'all' 
    ? notes 
    : notes.filter(note => note.category === categoryFilter);

  // Helper function to format scheduled time display
  const formatScheduledTime = (scheduledFor: Date) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const scheduledDate = new Date(scheduledFor.getFullYear(), scheduledFor.getMonth(), scheduledFor.getDate());
    const diffTime = scheduledDate.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    const timeString = scheduledFor.toTimeString().slice(0, 5);
    
    if (diffDays === 0) {
      return `today ${timeString}`;
    } else if (diffDays === 1) {
      return `tomorrow ${timeString}`;
    } else if (diffDays <= 30 && diffDays > 0) {
      return `${diffDays} days from now ${timeString}`;
    } else {
      return `scheduled: ${scheduledFor.toLocaleDateString()} ${timeString}`;
    }
  };

  return (
    <div className="min-h-screen bg-background p-4 flex flex-col pb-20" style={{backgroundColor: '#0f172a'}}>
      {/* Header */}
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-white">Notes</h1>
      </header>

      {/* Category Filter */}
      {shouldShowCategoryFilter && (
        <div className="mb-4">
          <div className="flex items-center gap-2 overflow-x-auto pb-2">
            <button
              onClick={() => setCategoryFilter('all')}
              className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                categoryFilter === 'all'
                  ? 'bg-blue-600 text-white'
                  : 'bg-white/10 text-gray-300 hover:bg-white/20'
              }`}
            >
              All ({notes.length})
            </button>
            {getUniqueCategories().map((category) => {
              const count = notes.filter(note => note.category === category).length;
              return (
                <button
                  key={category}
                  onClick={() => setCategoryFilter(category)}
                  className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                    categoryFilter === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-white/10 text-gray-300 hover:bg-white/20'
                  }`}
                >
                  {category} ({count})
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Add Note Button */}
      <div className="mb-6">
        <button
          onClick={() => setShowAddNote(!showAddNote)}
          className="w-full bg-white/10 border border-white/20 rounded-xl p-4 text-white font-medium hover:bg-white/20 transition-colors flex items-center justify-center gap-2"
        >
          <Plus size={20} />
          Add Note
        </button>
      </div>

      {/* Add Note Form */}
      <AnimatePresence>
        {showAddNote && (
          <motion.div 
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="mb-6 overflow-hidden"
          >
            <div className="bg-white/5 rounded-xl p-4 border border-white/10">
          {/* Category and Schedule Row */}
          <div className="flex gap-3 mb-4">
            {/* Category Selection */}
            <div className="relative flex-1">
              <button
                onClick={() => setShowCategoryDropdown(!showCategoryDropdown)}
                className="w-full bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white flex items-center justify-between hover:bg-white/20 transition-colors"
              >
                <span>{selectedCategory}</span>
                <ChevronDown size={16} className={`transition-transform ${showCategoryDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {/* Category Dropdown */}
              {showCategoryDropdown && (
                <div className="absolute top-full left-0 right-0 mt-1 bg-slate-800 border border-white/20 rounded-lg shadow-lg z-10">
                  {categories.map((category) => (
                    <button
                      key={category}
                      onClick={() => {
                        setSelectedCategory(category);
                        setShowCategoryDropdown(false);
                      }}
                      className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors first:rounded-t-lg last:rounded-b-lg ${
                        selectedCategory === category ? 'bg-white/20 text-white' : 'text-gray-300'
                      }`}
                    >
                      {category}
                    </button>
                  ))}
                  <button
                    onClick={() => setShowCategoryDropdown(false)}
                    className="w-full text-left px-3 py-2 text-sm text-blue-400 hover:bg-white/10 transition-colors rounded-b-lg border-t border-white/10"
                  >
                    + Add Category
                  </button>
                </div>
              )}
            </div>

            {/* Schedule Button */}
            <button 
              onClick={() => setShowSchedulePicker(!showSchedulePicker)}
              className={`bg-white/10 border border-white/20 rounded-lg px-3 py-2 text-sm text-white hover:bg-white/20 transition-colors flex items-center gap-2 ${
                (scheduledDate && scheduledTime) || showSchedulePicker ? 'bg-blue-600/20 border-blue-500/30' : ''
              }`}
            >
              <Calendar size={16} />
              {(scheduledDate && scheduledTime) ? 'scheduled' : 'schedule'}
            </button>
          </div>

          {/* Date/Time Picker */}
          <AnimatePresence>
            {showSchedulePicker && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                transition={{ duration: 0.2, ease: 'easeInOut' }}
                className="overflow-hidden mb-4"
              >
                <div className="bg-white/5 rounded-lg p-3 border border-white/10">
                  <div className="flex gap-3 mb-3">
                    <div className="flex-1">
                      <label className="block text-xs text-gray-400 mb-1">Date</label>
                      <input
                        type="date"
                        value={scheduledDate}
                        onChange={(e) => setScheduledDate(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                    </div>
                    <div className="flex-1">
                      <label className="block text-xs text-gray-400 mb-1">Time</label>
                      <input
                        type="time"
                        value={scheduledTime}
                        onChange={(e) => setScheduledTime(e.target.value)}
                        className="w-full bg-white/10 border border-white/20 rounded px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setScheduledDate('');
                        setScheduledTime('');
                        setShowSchedulePicker(false);
                      }}
                      className="flex-1 bg-white/10 border border-white/20 rounded py-1 text-xs text-gray-300 hover:bg-white/20 transition-colors"
                    >
                      Clear
                    </button>
                    <button
                      onClick={() => setShowSchedulePicker(false)}
                      className="flex-1 bg-blue-600 hover:bg-blue-700 rounded py-1 text-xs text-white transition-colors"
                    >
                      Done
                    </button>
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Text Area */}
          <div className="mb-4">
            <textarea
              value={noteContent}
              onChange={(e) => setNoteContent(e.target.value)}
              placeholder="e.g.: Eyecolor: blue"
              className="w-full bg-white/10 border border-white/20 rounded-lg p-3 text-white placeholder-gray-400 resize-none h-32 focus:outline-none focus:ring-2 focus:ring-blue-500/50"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <button
              onClick={handleCancelEdit}
              className="flex-1 bg-white/10 border border-white/20 rounded-lg py-2 text-sm text-gray-300 hover:bg-white/20 transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={editingNote ? handleUpdateNote : handleAddNote}
              className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-lg py-2 text-sm text-white font-medium transition-colors"
            >
              {editingNote ? 'Update Note' : 'Save Note'}
            </button>
          </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>



      {/* Notes List or Empty State */}
      <div className="flex-1">
        {filteredNotes.length > 0 ? (
          <div className="space-y-3">
            {filteredNotes
              .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
              .map((note) => (
              <div key={note.id} className="bg-white/5 border border-white/10 rounded-lg p-4 relative">
                <div className="flex justify-between items-start mb-2">
                  <div className="text-sm text-gray-400">{note.category}</div>
                  <div className="flex items-center gap-2">
                    <div className="text-xs text-gray-500">
                      {note.createdAt.toLocaleDateString()}
                    </div>
                    <button
                      onClick={() => handleEditNote(note.id)}
                      className="p-3 text-gray-400 hover:text-white transition-colors -m-2"
                    >
                      <Edit2 size={14} />
                    </button>
                    <button
                      onClick={() => handleDeleteNote(note.id)}
                      className="p-3 text-gray-400 hover:text-red-400 transition-colors -m-2"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>
                <div className="text-white pr-20">{note.content}</div>
                {note.scheduledFor && (
                  <div className="absolute bottom-4 right-4 text-xs text-blue-400">
                    {formatScheduledTime(new Date(note.scheduledFor))}
                  </div>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="py-8">
            <h3 className="text-lg font-medium text-white mb-4">
              {notes.length === 0 ? 'Empty State' : `No notes in ${categoryFilter === 'all' ? 'selected categories' : categoryFilter}`}
            </h3>
            {notes.length === 0 ? (
              <div className="text-gray-400 space-y-1">
                {emptyStateItems.map((item, index) => (
                  <div key={index} className="text-sm">{item}</div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400 text-sm">
                Try selecting a different category or create a new note.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Screen3;
